import { createTheme, alpha } from '@mui/material/styles';
import { brand } from './tokens';

// The app's theme: the landing page's navy, peach and type (tokens.js) at working density.
// Spacing is MUI's 8px unit; use the steps 0.5, 1, 1.5, 2, 3, 4, 6, 8 (4–64px).
// Motion is 150–250ms with the brand ease; prefers-reduced-motion turns it off.
const base = createTheme({
    palette: {
        primary: {
            main: brand.accent,
            light: brand.accentMid,
            dark: brand.accentHover,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: brand.highlight,
            light: brand.highlightTint,
            contrastText: brand.ink,
        },
        cdat: {
            teacher: {
                Q: '#1976d2',    // Questions (Blue)
                P: '#dc004e',    // Prompts (Red)
                E: '#4caf50',    // Examples/Explanations (Green)
                Fy: '#ff9800',   // Confirmatory Feedback (Orange)
                Fs: '#9c27b0',   // Feedback with Statements (Purple)
                Fq: '#00bcd4'    // Feedback with Questions (Cyan)
            },
            student: {
                Ry: '#4caf50',   // Confirmatory Response (Green)
                Rs: '#2196f3',   // Response with Statement (Blue)
                Rq: '#ff9800'    // Response with Question (Orange)
            }
        },
        text: {
            primary: brand.ink,
            secondary: brand.inkMuted,
        },
        background: {
            default: brand.subtle,
            paper: brand.paper,
        },
        divider: brand.lineStrong,
        action: {
            hover: alpha(brand.accent, 0.06),
            selected: alpha(brand.accent, 0.1),
        },
    },
    shape: {
        borderRadius: 8,
    },
    transitions: {
        easing: {
            easeInOut: brand.ease,
            easeOut: brand.ease,
        },
        duration: {
            shortest: 150,
            shorter: 150,
            short: 200,
            standard: 200,
            complex: 250,
            enteringScreen: 225,
            leavingScreen: 195,
        },
    },
    typography: {
        fontFamily: brand.body,
    },
});

const display = { fontFamily: brand.display, fontWeight: 700 };
const fast = `${base.transitions.duration.shortest}ms ${brand.ease}`;

const theme = createTheme(base, {
    // Type scale: page title (h4), section (h5), card (h6); body1 for reading, body2 for tables
    typography: {
        h1: { ...display, fontWeight: 800, fontSize: '2.25rem', lineHeight: 1.1, letterSpacing: '-0.035em' },
        h2: { ...display, fontSize: '1.875rem', lineHeight: 1.15, letterSpacing: '-0.03em' },
        h3: { ...display, fontSize: '1.5rem', lineHeight: 1.2, letterSpacing: '-0.025em' },
        h4: { ...display, fontSize: '1.375rem', lineHeight: 1.25, letterSpacing: '-0.02em' },
        h5: { ...display, fontSize: '1.125rem', lineHeight: 1.3, letterSpacing: '-0.015em' },
        h6: { ...display, fontSize: '1rem', lineHeight: 1.35, letterSpacing: '-0.01em' },
        subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5 },
        subtitle2: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.45 },
        body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
        body2: { fontSize: '0.875rem', lineHeight: 1.55 },
        caption: { fontSize: '0.75rem', lineHeight: 1.45 },
        overline: { fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.4 },
        button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                ':focus-visible': {
                    outline: `2px solid ${brand.accentMid}`,
                    outlineOffset: 2,
                },
                '@media (prefers-reduced-motion: reduce)': {
                    '*, *::before, *::after': {
                        transitionDuration: '0.01ms !important',
                        animationDuration: '0.01ms !important',
                    },
                },
            },
        },
        MuiAppBar: {
            defaultProps: {
                elevation: 0,
                color: 'inherit',
            },
            styleOverrides: {
                root: {
                    backgroundColor: brand.paper,
                    borderBottom: `1px solid ${brand.lineStrong}`,
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: `1px solid ${brand.lineStrong}`,
                },
            },
        },
        // Buttons by role (decision 2026-10-27): the main action has the landing page's pressable look at
        // app scale (rectangle with 6px corners, ink outline, 2px peach shadow it presses into); secondary is the same outline, flat;
        // text buttons stay flat. Code chips and reason pills are not buttons here and stay flat.
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    paddingInline: base.spacing(2.25),
                    transition: `background-color ${fast}, border-color ${fast}, color ${fast}, box-shadow ${fast}, transform ${fast}`,
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:hover, &:active': { transform: 'none' },
                    },
                },
                text: {
                    borderRadius: 6,
                    paddingInline: base.spacing(1.25),
                },
                contained: {
                    border: `1.5px solid ${brand.ink}`,
                    '--press': brand.highlight,
                    boxShadow: '2px 2px 0 var(--press)',
                    '&:hover': {
                        boxShadow: '1px 1px 0 var(--press)',
                        transform: 'translate(1px, 1px)',
                    },
                    '&:active': {
                        boxShadow: '0 0 0 var(--press)',
                        transform: 'translate(2px, 2px)',
                    },
                    '&.Mui-disabled': {
                        borderColor: brand.lineStrong,
                        boxShadow: 'none',
                    },
                },
                containedPrimary: {
                    '&:hover': { backgroundColor: brand.accentHover },
                },
                containedError: {
                    borderColor: '#5C1410',
                    '--press': '#F4C2C2',
                },
                outlined: {
                    borderWidth: '1.5px',
                    '&:hover': { borderWidth: '1.5px' },
                    '&.Mui-disabled': { borderWidth: '1.5px' },
                },
                outlinedPrimary: {
                    color: brand.ink,
                    borderColor: brand.ink,
                    '&:hover': { borderColor: brand.ink, backgroundColor: brand.subtle },
                },
                outlinedError: {
                    borderColor: alpha('#D32F2F', 0.8),
                },
                sizeSmall: {
                    paddingInline: base.spacing(1.5),
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    transition: `background-color ${fast}, color ${fast}`,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontWeight: 500,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    marginTop: base.spacing(0.5),
                    borderRadius: 10,
                    boxShadow: '0 4px 12px rgba(11, 18, 32, 0.08), 0 16px 32px -16px rgba(30, 58, 95, 0.18)',
                },
                list: {
                    padding: base.spacing(0.75),
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    minHeight: 40,
                    borderRadius: 6,
                    fontSize: '0.875rem',
                    transition: `background-color ${fast}`,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 14,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 600,
                    color: brand.inkMuted,
                    backgroundColor: brand.subtle,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: brand.ink,
                    fontSize: '0.75rem',
                },
            },
        },
    },
});

export default theme;
